// frontend/src/components/auth/LoginForm.tsx
// ✅ UPDATED: Using new refactored hooks

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, Loader2, CheckCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLogin, useResendVerificationEmail } from '@/hooks/useAuth';

interface LoginFormProps {
  className?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ className = '' }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember_me: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  // Auth context for state only
  // const { user } = useAuth(); // Not needed in this component
  
  // New hooks for actions
  const loginMutation = useLogin();
  const resendVerificationMutation = useResendVerificationEmail();
  
  const navigate = useNavigate();
  const location = useLocation();

  // Get any messages from navigation state
  const stateMessage = location.state?.message;
  const messageType = location.state?.type || 'info';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setNeedsVerification(false);

    try {
      await loginMutation.mutateAsync(formData);
      
      // Successful login - navigate to intended page or dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
      
    } catch (err: any) {
      // Handle different error types
      if (err.message?.includes('verification') || err.message?.includes('verify')) {
        setNeedsVerification(true);
        setErrors({ verification: 'Please verify your email address to continue.' });
      } else {
        setErrors({ general: err.message || 'Login failed. Please try again.' });
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear errors when user starts typing
    if (errors[name] || errors.general || errors.verification) {
      setErrors({});
      setNeedsVerification(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setErrors({ verification: 'Please enter your email address first.' });
      return;
    }

    try {
      await resendVerificationMutation.mutateAsync();
      setVerificationResent(true);
      setErrors({});
    } catch (err: any) {
      setErrors({ verification: err.message || 'Failed to resend verification email.' });
    }
  };

  const isLoading = loginMutation.isPending;
  const isResendingVerification = resendVerificationMutation.isPending;

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <Lock className="h-6 w-6 text-blue-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your atmyarea dashboard
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* State message */}
          {stateMessage && (
            <div className={`rounded-md p-4 ${
              messageType === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : messageType === 'error' 
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex">
                <CheckCircle className={`h-5 w-5 ${
                  messageType === 'success' ? 'text-green-400' : 'text-blue-400'
                }`} />
                <div className="ml-3">
                  <p className={`text-sm ${
                    messageType === 'success' 
                      ? 'text-green-800' 
                      : messageType === 'error' 
                        ? 'text-red-800'
                        : 'text-blue-800'
                  }`}>
                    {stateMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* General error */}
          {errors.general && (
            <div className="rounded-md bg-red-50 border border-red-200 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              </div>
            </div>
          )}

          {/* Email verification error */}
          {needsVerification && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    {errors.verification || 'Please verify your email address to continue.'}
                  </p>
                  {verificationResent ? (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ Verification email resent successfully!
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={isResendingVerification}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-500 disabled:opacity-50"
                    >
                      {isResendingVerification ? (
                        <>
                          <RefreshCw className="inline h-4 w-4 animate-spin mr-1" />
                          Sending...
                        </>
                      ) : (
                        'Resend verification email'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember_me"
                  name="remember_me"
                  type="checkbox"
                  checked={formData.remember_me}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              Don't have an account?
            </p>
            <div className="flex justify-center space-x-4">
              <Link
                to="/register/customer"
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
              >
                Sign up as Customer
              </Link>
              <span className="text-gray-300">|</span>
              <Link
                to="/register/partner"
                className="font-medium text-green-600 hover:text-green-500 transition-colors duration-200"
              >
                Become a Partner
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;